const ALLOWED_ELEMENTS = new Set([
    "a",
    "abbr",
    "address",
    "b",
    "blockquote",
    "br",
    "caption",
    "code",
    "col",
    "colgroup",
    "dd",
    "del",
    "div",
    "dl",
    "dt",
    "em",
    "figcaption",
    "figure",
    "font",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "img",
    "ins",
    "kbd",
    "li",
    "mark",
    "ol",
    "p",
    "pre",
    "q",
    "s",
    "samp",
    "section",
    "small",
    "span",
    "strong",
    "sub",
    "sup",
    "table",
    "tbody",
    "td",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
    "var",
]);

const REMOVE_WITH_CONTENT = new Set([
    "audio",
    "button",
    "embed",
    "form",
    "iframe",
    "input",
    "math",
    "object",
    "script",
    "select",
    "style",
    "svg",
    "template",
    "textarea",
    "video",
]);

export interface SanitizeCommerceHtmlOptions {
    readonly allowLinks?: boolean;
}

export function sanitizeCommerceHtml(
    value: string,
    options: SanitizeCommerceHtmlOptions = {},
): string {
    const template = document.createElement("template");
    template.innerHTML = value;

    for (const sourceElement of Array.from(template.content.querySelectorAll("*"))) {
        let element = sourceElement;
        let name = element.localName.toLowerCase();
        if (!ALLOWED_ELEMENTS.has(name)) {
            if (REMOVE_WITH_CONTENT.has(name)) element.remove();
            else element.replaceWith(...Array.from(element.childNodes));
            continue;
        }
        if (name === "a" && options.allowLinks === false) {
            const nonInteractive = document.createElement("span");
            for (const attribute of Array.from(element.attributes)) {
                if (isSafeGlobalAttribute(attribute.name)) {
                    nonInteractive.setAttribute(attribute.name, attribute.value);
                }
            }
            nonInteractive.append(...Array.from(element.childNodes));
            element.replaceWith(nonInteractive);
            element = nonInteractive;
            name = "span";
        }

        for (const attribute of Array.from(element.attributes)) {
            const allowed =
                isSafeGlobalAttribute(attribute.name) ||
                (name === "a" &&
                    (attribute.name === "href" ||
                        attribute.name === "title" ||
                        attribute.name === "target" ||
                        attribute.name === "rel")) ||
                (name === "img" &&
                    ["src", "alt", "title", "width", "height", "loading"].includes(
                        attribute.name,
                    )) ||
                (["td", "th"].includes(name) &&
                    ["colspan", "rowspan", "scope", "headers"].includes(attribute.name)) ||
                (["col", "colgroup"].includes(name) && attribute.name === "span") ||
                (["ol", "li"].includes(name) &&
                    ["start", "type", "value"].includes(attribute.name)) ||
                (name === "font" && ["color", "face", "size"].includes(attribute.name));
            if (!allowed) element.removeAttribute(attribute.name);
        }

        sanitizeInlineStyle(element);

        if (name === "a") {
            const href = element.getAttribute("href");
            if (href !== null && !isSafeLink(href)) element.removeAttribute("href");
            if (element.getAttribute("target") === "_blank") {
                element.setAttribute("rel", "noopener noreferrer");
            } else {
                element.removeAttribute("target");
            }
        } else if (name === "img") {
            const src = element.getAttribute("src");
            if (src === null || !isSafeResource(src)) element.remove();
        }
    }

    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_COMMENT);
    const comments: Comment[] = [];
    while (walker.nextNode()) comments.push(walker.currentNode as Comment);
    comments.forEach((comment) => comment.remove());

    return template.innerHTML;
}

function isSafeGlobalAttribute(name: string): boolean {
    return (
        name === "style" ||
        name === "class" ||
        name === "id" ||
        name === "dir" ||
        name === "lang" ||
        name === "title" ||
        name === "role" ||
        name.startsWith("aria-") ||
        name.startsWith("data-")
    );
}

function sanitizeInlineStyle(element: Element): void {
    const style = (element as HTMLElement).style;
    if (style === undefined || element.getAttribute("style") === null) return;

    const properties: string[] = [];
    for (let index = 0; index < style.length; index += 1) {
        properties.push(style.item(index));
    }

    for (const property of properties) {
        const value = style.getPropertyValue(property);
        if (isUnsafeCssDeclaration(property, value)) style.removeProperty(property);
    }

    if (style.length === 0) element.removeAttribute("style");
}

function isUnsafeCssDeclaration(property: string, value: string): boolean {
    const normalizedProperty = decodeCssEscapes(property).replace(/\s+/g, "").toLowerCase();
    const normalizedValue = decodeCssEscapes(value).replace(/\s+/g, "").toLowerCase();
    return (
        normalizedProperty === "behavior" ||
        normalizedProperty === "-moz-binding" ||
        normalizedValue.includes("expression(") ||
        normalizedValue.includes("javascript:") ||
        normalizedValue.includes("vbscript:") ||
        normalizedValue.includes("@import") ||
        normalizedValue.includes("progid:")
    );
}

function decodeCssEscapes(value: string): string {
    return value
        .replace(/\\([0-9a-f]{1,6})\s?/gi, (_match, hex: string) =>
            String.fromCodePoint(Number.parseInt(hex, 16)),
        )
        .replace(/\\(.)/g, "$1");
}

function isSafeResource(value: string): boolean {
    const trimmed = value.trim();
    if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return true;
    try {
        const url = new URL(trimmed, "https://commerce.invalid");
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function isSafeLink(value: string): boolean {
    const trimmed = value.trim();
    if (trimmed.startsWith("#") || trimmed.startsWith("/") || trimmed.startsWith("?")) return true;
    try {
        const url = new URL(trimmed, "https://commerce.invalid");
        return (
            url.protocol === "http:" ||
            url.protocol === "https:" ||
            url.protocol === "mailto:" ||
            url.protocol === "tel:"
        );
    } catch {
        return false;
    }
}
